/* eslint-disable react/prop-types */
import { cableName } from "@/constants";
import { Fingerprint, Loader2, ScanFace } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { cn } from "@/lib/utils";
import CurrencyInput from "react-currency-input-field";
import { useSelector } from "react-redux";
import { PostRequest } from "@/lib/http";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Loading } from "notiflix";
import { NativeBiometric } from "@capgo/capacitor-native-biometric";
import { Button } from "../ui/button";
import { Capacitor } from "@capacitor/core";

const BuyCableForm = ({ metadata, allPlans, beneficiaryData }) => {
  const user = useSelector((state) => state.user.user);
  const [isLoading, setIsLoading] = useState(false);
  const [isIUCChecking, setIsIUCChecking] = useState(false);
  const [iucStatus, setIUCStatus] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false)
  // Add a state to control the select component

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      number: "",
      company: "",
      plan: "",
      type: "",
      cost: 0,
      provider: [],
      subtype: "renew",
      savebeneficiary: false,
    },
  });

  const platform = Capacitor.getPlatform();

  const onSubmit = async (body) => {
    Loading.standard("Processing payment...");
    const { data, err } = await PostRequest({
      url: "/api/buyCable",
      body,
    });
    if (!err) {
      toast.success("Payment successful");
    } else {
      toast.error(err);
    }
    Loading.remove();
  };

  const validateIUC = async () => {
    const number = watch("number");
    const company = watch("company");
    setIUCStatus(null);
    if (number && company) {
      const { data, err } = await PostRequest({
        url: "/api/checkIUC",
        body: {
          meter_number: number,
          cabler_id: company,
        },
        setState: setIsIUCChecking,
      });
      if (!err) {
        setIUCStatus(data);
      } else {
        toast.error(err);
      }
    } else {
      toast.error(
        "Please select a cable provider and ensure IUC number is entered",
      );
    }
  };

  const plans = allPlans.filter((plan) =>
    plan.cabler.toLowerCase().includes(watch("company").toLowerCase()),
  );

  async function authenticateWithBiometrics() {
    const result = await NativeBiometric.isAvailable();

    if (!result.isAvailable) {
      toast.warning("Biometric authentication is not available on this device. Kindly enable biometrics in your settings.");
    }

    const verified = await NativeBiometric.verifyIdentity({
      reason: "Authorize Transaction",
      title: "Cable Subscription Purchase",
      subtitle: "Please authenticate to proceed with this transaction.",
    }).then(() => true)
      .catch(() => false);

    if (verified) {
      setValue("pin", user.pin.toString())
      setDrawerOpen(true)
    } else {
      toast.error("Authentication failed. Please try again.");
    }
  }

  // Fix for beneficiary data handling
  useEffect(() => {
    if (beneficiaryData) {
      setValue("number", beneficiaryData.value);

      if (beneficiaryData.disco) {
        const networkName = beneficiaryData.disco.toLowerCase();

        const carrier = cableName.find(
          (cable) =>
            networkName.includes(cable.name.toLowerCase()) ||
            cable.name.toLowerCase().includes(networkName),
        );

        setValue("company", carrier.name);
      }
    }
  }, [beneficiaryData, setValue]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <section className="grid grid-cols-1 gap-2">
        <div className="col-span-1">
          <div className="col-span-1">
            <label htmlFor="cable-name" className="text-sm font-semibold">
              Cable Name
            </label>
            <select
              value={watch("company")}
              onChange={(e) => setValue("company", e.target.value)}
              className="inputClass"
            >
              <option value="">Select cable provider</option>
              {cableName.map((cable) => (
                <option key={cable.name} value={cable.name}>
                  {cable.name}
                </option>
              ))}
            </select>
            {errors.company && (
              <span className="text-xs font-medium text-red-500">
                {errors.company.message}
              </span>
            )}
          </div>
          {errors.company && (
            <span className="text-xs font-medium text-red-500">
              {errors.company.message}
            </span>
          )}
        </div>

        {/* Rest of your form remains the same */}
        <div className="col-span-1">
          <label htmlFor="number" className="text-sm font-semibold">
            IUC Number
          </label>
          <div className="dark:border-darkBorder dark:bg-darkInput mt-1 flex items-center gap-x-3 rounded-md border bg-gray-100 p-2 py-3">
            <input
              type="text"
              disabled={isIUCChecking}
              {...register("number", {
                required: "IUC number is required",
              })}
              className="w-full flex-1 border-none bg-transparent outline-none"
              placeholder="738-288-1681"
            />
            <button
              onClick={validateIUC}
              className="bg-primary cursor-pointer rounded p-1 text-sm font-medium text-white disabled:opacity-50"
              type="button"
              disabled={isIUCChecking}
            >
              {isIUCChecking ? "Checking..." : "Validate IUC"}
            </button>
          </div>
          {iucStatus}

          {!beneficiaryData && (
            <div className="mt-2 flex items-center gap-2">
              <input
                type="checkbox"
                id="savebeneficiary"
                checked={watch("savebeneficiary")}
                onChange={() => {
                  setValue("savebeneficiary", !watch("savebeneficiary"));
                }}
              />
              <label htmlFor="savebeneficiary">Save As Beneficiary</label>
            </div>
          )}

          {errors.number && (
            <span className="text-xs font-medium text-red-500">
              {errors.number.message}
            </span>
          )}
        </div>

        {iucStatus ? (
          <>
            <div className="col-span-1">
              <label htmlFor="cable-plan" className="text-sm font-semibold">
                Cable Plan
              </label>
              <Select
                onValueChange={(value) => {
                  const selectedPlan = plans.find(
                    (p) => p.id === parseInt(value),
                  );
                  setValue("plan", value);
                  setValue("amount", selectedPlan?.price || "");
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select cable plan" />
                </SelectTrigger>
                <SelectContent>
                  {plans.map((plan) => (
                    <SelectItem key={plan.id} value={plan.id.toString()}>
                      {plan.name} - ₦{parseFloat(plan.price).toLocaleString()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.plan && (
                <span className="text-xs font-medium text-red-500">
                  {errors.plan.message}
                </span>
              )}
            </div>
            <div className="col-span-1">
              <label htmlFor="amount" className="text-sm font-semibold">
                Amount
              </label>
              <div className="dark:border-darkBorder dark:bg-darkInput mt-1 flex items-center gap-x-3 rounded-md border bg-gray-100 p-2 py-2.5">
                <CurrencyInput
                  prefix="₦ "
                  readOnly
                  value={watch("amount")}
                  className="w-full border-none bg-transparent text-sm outline-none"
                  placeholder="Amount to transfer"
                />
              </div>
              {errors.amount && (
                <span className="text-xs font-medium text-red-500">
                  {errors.amount.message}
                </span>
              )}
            </div>
            <div className="col-span-1">
              <label htmlFor="pin" className="text-sm font-semibold">
                Transaction Pin
              </label>
              <div className="dark:border-darkBorder dark:bg-darkInput mt-1 flex items-center gap-x-3 rounded-md border bg-gray-100 p-2 py-2.5">
                <div className="flex">
                  <input
                    type="password"
                    {...register("pin", {
                      required: "Pin is required",
                      pattern: {
                        value: /^[0-9]+$/,
                        message: "Please enter numbers only",
                      },
                    })}
                    autoComplete="off"
                    inputMode="numeric"
                    className="w-full border-none bg-transparent text-sm outline-none"
                    placeholder="Enter pin"
                  />
                  {platform != "web" && <Button onClick={authenticateWithBiometrics} type="button" className={"h-[40px] text-white"}>
                    {platform == "android" ? <Fingerprint size={30} /> : <ScanFace size={30} />} Use Biometrics
                  </Button>}
                </div>
              </div>
              {errors.pin && (
                <span className="text-xs font-medium text-red-500">
                  {errors.pin.message}
                </span>
              )}
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className={cn(
                "btn mt-2 w-full rounded-md py-3 font-semibold text-white disabled:pointer-events-none disabled:bg-gray-600",
                {
                  "bg-primary": isLoading,
                },
              )}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="animate-spin" />
                  Purchasing...
                </span>
              ) : (
                "Purchase"
              )}
            </button>
          </>
        ) : (
          <div className="text-center">Please validate IUC to continue</div>
        )}
      </section>
    </form>
  );
};

export default BuyCableForm;
